<script setup lang="ts">
import { useDataFormatter } from "@/composable/data-formatter";
import { StatusSourceType } from "@/stores";
import { CartesianChartOptions, LineChart, LinePathOptions } from "@soft-db/d3-chart";
import { DataDefinition } from "@soft-db/data-formatter";
import { StatusDataType } from "@soft-db/monitoring-api-client";
import { Badge, BaseColor, LocalScope, Tooltip } from "@soft-db/ui";
import { computed, ref, watch } from "vue";
import { useLiveDataBadge } from "../status/composable/live-data-badge";
import { useLiveStatus } from "../status/composable/live-status";

const MAX_NUMBER_OF_DATA = 10;

const { t } = useI18n();

const props = defineProps<{
	sourceType: StatusSourceType;
	sourceId: string;
}>();

const liveStatus = useLiveStatus(props.sourceType, () => props.sourceId);

const data = ref<(number | null)[]>(new Array(MAX_NUMBER_OF_DATA).fill(null));
const min = ref(Number.MAX_SAFE_INTEGER);
const max = ref(Number.MIN_SAFE_INTEGER);

const { convertTo } = useDataFormatter();

const dataDefinition = computed<DataDefinition>(() => {
	switch (liveStatus.value?.type) {
		case StatusDataType.ACC:
			return {
				type: "vibration",
				subType: "acceleration",
				data: "rms",
				axis: "v_sum",
				unit: "mm/s²",
			};
		case StatusDataType.VEL:
			return {
				type: "vibration",
				subType: "velocity",
				data: "rms",
				axis: "v_sum",
				unit: "mm/s",
			};
		case StatusDataType.OPL:
			return { type: "overpressure", data: "peak", unit: "dB" };

		default:
			return { type: "sound", data: "leq", ponderation: "a", unit: "dB" };
	}
});

const options = computed<CartesianChartOptions>(() => ({
	axes: [
		{
			side: "bottom",
			type: "linear",
			visible: false,
			range: {
				min: 2,
				max: MAX_NUMBER_OF_DATA,
			},
		},
		{
			side: "left",
			type: "linear",
			range: {
				min: convertTo(min.value * 0.8, dataDefinition.value),
				max: convertTo(max.value * 1.2, dataDefinition.value),
			},
		},
	],
	layout: {
		margins: {
			top: 0,
			bottom: 0,
			left: 0,
			right: 0,
		},
	},
	live: true,
	animation: true,
	tooltip: true,
	cursor: {
		x: {
			live: true,
		},
	},
}));

const serie = computed<LinePathOptions>(() => ({
	id: "live-chart",
	style: {
		color: "rgb(124,77,255)",
		lineWidth: 2.5,
		antialiasing: true,
		point: {
			visible: false,
			radius: 0,
			shape: "circle",
			size: 2,
		},
	},
	data: data.value.map((v, i) => ({
		x: i + 2,
		y: v != null ? convertTo(v, dataDefinition.value) : v,
	})),
	xAxis: "bottom",
	yAxis: "left",
}));

function extractValueAt(idx: number) {
	return serie.value.data.at(Math.floor(idx))?.y ?? null;
}

type LiveStatusState = "live" | "no-signal";

const liveStatusState = computed<LiveStatusState>(() => {
	if (liveStatus.value == null) {
		return "no-signal";
	}

	if (liveStatus.value.value == null) {
		return "no-signal";
	}

	return "live";
});

const liveDataBadge = useLiveDataBadge(liveStatus);

type LiveDataStatus = {
	color: BaseColor;
	text: string;
};
const liveDateStatus = computed<LiveDataStatus>(() => {
	switch (liveStatusState.value) {
		case "live":
			return {
				color: "success",
				text: t("live-status.liveSignal"),
			};
		case "no-signal": {
			return {
				color: "error",
				text: t("live-status.noSignal"),
			};
		}
	}
});

// The counter is used to force the chart to move every second
const counter = useInterval(1_000);
watch(
	counter,
	() => {
		if (liveStatus.value) {
			const { value } = liveStatus.value;
			data.value.push(value ?? null);

			const validData = data.value.filter((d): d is number => d != null);

			min.value = Math.min(...validData);
			max.value = Math.max(...validData);

			if (data.value.length > MAX_NUMBER_OF_DATA) {
				data.value.shift();
			}
		}
	},
	{ immediate: true }
);

watch(
	() => props.sourceId,
	(newValue, oldValue) => {
		if (newValue !== oldValue && oldValue !== undefined) {
			data.value = [];
			min.value = Number.MAX_SAFE_INTEGER;
			max.value = Number.MIN_SAFE_INTEGER;
		}
	},
	{ immediate: true }
);
</script>

<template>
	<div class="relative size-full">
		<LineChart :options="options" :series="[serie]">
			<template #tooltip="{ bottom }">
				<LocalScope v-slot="{ value }" :value="extractValueAt(bottom)">
					<Tooltip v-if="value != null" open followCursor :offset="8" placement="bottom-start">
						<template #tooltip>
							{{ liveDataBadge?.name }}: {{ value.toFixed(2) }} {{ dataDefinition.unit }}
						</template>
					</Tooltip>
				</LocalScope>
			</template>
		</LineChart>

		<div class="text-muted absolute right-2 top-2 flex w-full justify-end">
			<Badge class="flex items-center gap-1" :color="liveDateStatus.color" variant="soft" size="sm">
				<div
					class="size-2 rounded-full"
					:class="{
						'bg-success': liveDateStatus.color === 'success',
						'bg-warning': liveDateStatus.color === 'warning',
						'bg-error': liveDateStatus.color === 'error',
					}"
				></div>
				{{ liveDateStatus.text }}
			</Badge>
		</div>
	</div>
</template>
